/**
 * Class representing the UFO tractor beam animation effect for the cow,
 * responding to scroll progress with smooth easing and rotation.
 */
export default class UfoCowAnimation {
    /**
     * Creates an instance of UfoCowAnimation.
     * @param {string|HTMLElement} [sectionSelector='#manifest'] - The main section selector or element.
     */
    constructor(sectionSelector) {
        this.section = typeof sectionSelector === "string" ? document.querySelector(sectionSelector) : sectionSelector;
        if (!this.section) return;

        this.box = this.section.querySelector(".content-ufo");
        this.cow = this.section.querySelector(".ufo-cow");

        // Respect user's reduced motion preferences
        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        if (!this.box || !this.cow || prefersReducedMotion) return;

        this.currentProgress = 0;
        this.rafId = null;

        this.init();
    }

    /**
     * Initializes the animation loop.
     */
    init() {
        const render = () => {
            const rect = this.section.getBoundingClientRect();
            const viewportHeight = window.innerHeight;

            // Skip calculations if the section is far outside the viewport
            if (rect.bottom < -200 || rect.top > viewportHeight + 200) {
                this.rafId = requestAnimationFrame(render);
                return;
            }

            const boxRect = this.box.getBoundingClientRect();

            // Calculate progress: starts when UFO enters screen, finishes at the upper third
            const rawProgress = (viewportHeight * 0.5 - boxRect.top) / (viewportHeight * 0.6);
            const targetProgress = Math.max(0, Math.min(1, rawProgress));

            // Soft lagging / damping effect without jitter
            this.currentProgress += (targetProgress - this.currentProgress) * 0.03;

            // Smoothstep easing function
            const eased = this.currentProgress * this.currentProgress * (3 - 2 * this.currentProgress);

            const boxHeight = this.box.offsetHeight;
            const translateY = -eased * boxHeight * 0.48;
            const scale = 1 - eased * 0.6;
            const rotation = Math.sin(this.currentProgress * Math.PI * 2) * 14 * eased;

            // Apply transforms via hardware-accelerated style
            this.cow.style.transform = `translate3d(0, ${translateY.toFixed(1)}px, 0) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(3)})`;

            this.rafId = requestAnimationFrame(render);
        };

        this.rafId = requestAnimationFrame(render);
    }

    /**
     * Cleans up the animation frame loop.
     */
    destroy() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    /**
     * Automatically initializes the animation if the section exists on the page.
     * @param {string} [selector='#manifest'] - Section selector.
     * @returns {UfoCowAnimation|null} Instance or null if element is missing.
     */
    static init(selector = "#manifest") {
        const element = document.querySelector(selector);
        return element ? new UfoCowAnimation(element) : null;
    }
}
